"use client"

// Route-level error boundary (FRIDGE-177).
//
// WHY THIS FILE EXISTS
//
// Until it did, this app had NO error boundary of any kind — no error.tsx, no
// global-error.tsx, and no class boundary (`componentDidCatch` /
// `getDerivedStateFromError`) anywhere in app/, components/ or lib/. A React
// render error therefore fell through to the framework default, and the visitor
// got an unbranded screen on a site whose standard is that a bare-minimum page
// is beneath it.
//
// The worse half was silent: Sentry is wired for server, edge and client (five
// config files plus instrumentation), but React RENDER errors do not reach any
// of those hooks. Sentry's own build output said so on every single build:
//
//   "It seems like you don't have a global error handler set up. It is
//    recommended that you add a 'global-error.js' file with Sentry
//    instrumentation so that React rendering errors are reported to Sentry."
//
// So the one tool bought to answer "when this breaks, does anyone find out?"
// could not see this entire class of failure. On ~91,157 pages a render error
// in one page type means visitors see a broken screen, crawlers get a 500 —
// and nobody is told.
//
// ── WHAT IS DELIBERATELY NOT SHOWN ─────────────────────────────────────────
// `error.message` is NOT rendered. In a production build React redacts server
// messages to a generic string, but a client-side render error keeps its real
// message, which can carry internal detail. `error.digest` IS shown: it is an
// opaque identifier Next.js assigns, safe to display, and it is the value that lets a
// report be matched to the Sentry event and the server log.

import { useEffect } from 'react'
import Link from 'next/link'
import { SiteHeader } from '@/components/site-header'
import { SiteFooter } from '@/components/site-footer'
import { Button } from '@/components/ui/button'
import { RefreshCw, ChevronRight } from 'lucide-react'

// Curated destinations, mirroring components/blocks/not-found-state. Slugs are
// the ones already verified live there, so an error page never sends a shopper
// into a second dead end.
const TOP_COLLECTIONS: Array<{ slug: string; label: string }> = [
  { slug: 'refrigerators', label: 'Refrigerators' },
  { slug: 'french-door-refrigerators', label: 'French Door' },
  { slug: 'mini-fridges', label: 'Mini Fridges' },
  { slug: 'freezers', label: 'Freezers' },
  { slug: 'wine-coolers', label: 'Wine Coolers' },
  { slug: 'ice-makers', label: 'Ice Makers' },
]

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // The whole point of the file. Without this call the error is invisible to
    // Sentry no matter how completely the SDK is otherwise configured.
    // Loaded on demand: the monitoring SDK is fetched after consent by
    // instrumentation-client.ts, and this resolves to that same module. A static
    // import here put the SDK core in every route's first-load script.
    void import('@sentry/nextjs')
      .then((Sentry) => { Sentry.captureException(error) })
      .catch(() => { /* reporting is best-effort */ })
  }, [error])

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Something went wrong on this page
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            This one is on us, not on you. Our team has already been alerted.
            Try again — and if it keeps happening, the links below will get you moving.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button onClick={reset} size="lg" className="gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link href="/collections/refrigerators">Browse Refrigerators</Link>
            </Button>
          </div>

          <div className="mt-12 border-t border-gray-200 pt-8">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Popular collections
            </h2>
            <ul className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2">
              {TOP_COLLECTIONS.map((c) => (
                <li key={c.slug}>
                  <Link
                    href={`/collections/${c.slug}`}
                    className="inline-flex items-center text-gray-700 underline-offset-4 hover:text-gray-900 hover:underline"
                  >
                    {c.label}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {error.digest ? (
            <p className="mt-10 text-sm text-gray-500">
              Reference: <code className="font-mono">{error.digest}</code>
            </p>
          ) : null}
        </div>
      </div>
      <SiteFooter />
    </div>
  )
}
